import EngineCleanupBase from '../../../services/engines/EngineCleanupBase';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import attachmentCache from './AttachmentCache';
import AttachmentFetcher from './AttachmentFetcher';

const _name = 'AttachmentEngine';

// TabEngine notifies on this name — used to clear cache on tab switch
const _tabEngineName = 'TabEngine';

/**
 * Orchestrates document attachments per journal entry, per panel.
 * Cache is keyed on jid — shared across panels showing the same entry.
 * Entry tracking is keyed on panelId — each panel has its own current jid.
 * Singleton — one instance shared across the app.
 */
class AttachmentEngine extends EngineCleanupBase {
    _fetcher = new AttachmentFetcher();
    _entries = new Map(); // panelId → jid
    _unsubTabChanged = null;

    bootup = () => {
        this.initialize(_name);
        // Subscribe to tab changes — clear default panel cache on tab switch
        this._unsubTabChanged = ComponentNotifier.subscribe(_tabEngineName, this._onTabChanged);
    };

    /**
     * Called when user selects a new entry in a specific panel.
     * Clears cache for previous entry, triggers fetch for new jid.
     * @param {string|null} jid
     * @param {string} [panelId='default']
     */
    setEntry = (jid, panelId = 'default') => {
        try {
            if (!jid) {
                this._entries.set(panelId, null);
                this._notify(panelId);
                return;
            }

            this._entries.set(panelId, jid);
            this._notify(panelId);

            // Only fetch if not already cached for this jid
            if (!attachmentCache.getByJid(jid)?.length) {
                this._fetchDocuments(jid);
            }
        } catch (error) {
            this.logError('setEntry', error.message);
        }
    };

    /**
     * Called from SignalR OnDocumentsReceived — fills cache async.
     * Notifies all panels that are currently showing this jid.
     * @param {object[]} documents
     */
    onDocumentsReceived = (documents) => {
        try {
            if (!Array.isArray(documents) || !documents.length) return;
            attachmentCache.addOrUpdate(documents);
            // Determine which jid was updated — all docs share the same jid
            const jid = documents[0]?.jid;
            if (jid) this._notifyByJid(jid);
        } catch (error) {
            this.logError('onDocumentsReceived', error.message);
        }
    };

    /**
     * Returns cached documents for the given panel's current jid.
     * @param {string} [panelId='default']
     * @returns {object[]}
     */
    getDocuments = (panelId = 'default') => {
        const jid = this._entries.get(panelId);
        if (!jid) return [];
        return attachmentCache.getByJid(jid);
    };

    /**
     * Returns the current jid for the given panel.
     * Used by AttachmentStripHandler to tag fake docs on upload failure.
     * @param {string} [panelId='default']
     * @returns {string|null}
     */
    getCurrentJid = (panelId = 'default') => {
        return this._entries.get(panelId) ?? null;
    };

    /**
     * Removes a document from cache after successful BE delete.
     * Notifies all panels that may be showing this document.
     * @param {string} rk - Document RK
     */
    removeDocument = (rk) => {
        try {
            attachmentCache.delete(rk);
            // Notify all known panels — any of them could be showing this doc
            for (const panelId of this._entries.keys()) {
                this._notify(panelId);
            }
        } catch (error) {
            this.logError('removeDocument', error.message);
        }
    };

    // ─── private ──────────────────────────────────────────

    /**
     * Silently resets the default panel on tab switch.
     * Floating panels manage their own lifecycle via setEntry.
     */
    _onTabChanged = () => {
        try {
            this._entries.set('default', null);
        } catch (error) {
            this.logError('_onTabChanged', error.message);
        }
    };

    /**
     * Temporary REST fallback — replaced by SignalR RequestDocuments later.
     * @param {string} jid
     */
    _fetchDocuments = async (jid) => {
        try {
            const items = await this._fetcher.fetch(jid);
            if (!items?.length) return;
            attachmentCache.addOrUpdate(items);
            this._notifyByJid(jid);
        } catch (error) {
            this.logError('_fetchDocuments', error.message);
        }
    };

    /**
     * Notifies a specific panel of a data change.
     * @param {string} panelId
     */
    _notify = (panelId) => {
        ComponentNotifier.notify('dataChanged', _name, {
            panelId,
            documents: this.getDocuments(panelId),
        });
    };

    /**
     * Notifies all panels currently showing the given jid.
     * Used after async fetch or SignalR push — don't know which panel triggered it.
     * @param {string} jid
     */
    _notifyByJid = (jid) => {
        for (const [panelId, pJid] of this._entries) {
            if (pJid === jid) this._notify(panelId);
        }
    };
}

const attachmentEngine = new AttachmentEngine();
export default attachmentEngine;
